import { useEffect, useRef, useState } from 'react'

import gsap from 'gsap'

import type { FC } from 'react'

export interface AnimatedScoreProps {
  value: number
  className?: string
  style?: React.CSSProperties
}

const ANIMATION_DURATION = 0.3

const AnimatedScore: FC<AnimatedScoreProps> = ({ value, className, style }) => {
  const elementRef = useRef<HTMLSpanElement>(null)
  const previousValueRef = useRef<number | null>(null)
  const isFirstRenderRef = useRef(true)
  const [displayValue, setDisplayValue] = useState(value)

  useEffect(() => {
    // Skip animation on first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      previousValueRef.current = value
      return
    }

    // Only animate if value actually changed
    if (previousValueRef.current === value) {
      return
    }

    previousValueRef.current = value

    const element = elementRef.current
    if (!element) return

    // Create timeline: fade out → update value → fade in
    const tl = gsap.timeline()

    tl.to(element, {
      opacity: 0,
      duration: ANIMATION_DURATION,
      ease: 'power2.inOut',
      onComplete: () => {
        setDisplayValue(value)
      },
    }).to(element, {
      opacity: 1,
      duration: ANIMATION_DURATION,
      ease: 'power2.inOut',
    })

    return () => {
      tl.kill()
    }
  }, [value])

  return (
    <span ref={elementRef} className={className} style={style}>
      {displayValue}
    </span>
  )
}

export default AnimatedScore
